import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import LogoutIcon from '@mui/icons-material/Logout';
import { Avatar, Box, Button, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Popover, Stack, Modal, Typography, useTheme } from '@mui/material';
import React from 'react';
import { AuthorizationAPI } from '../apis';
import { constants, ROUTES } from '../constants';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import SettingsModal from './SettingsModal';
import { clearCache } from '../utils/indexedDBCache';
import HelpCenterIcon from '../../public/HelpCenter.svg';
import ReviewsIcon from '../../public/Reviews.svg';
import SettingsIcon from '../../public/Settings.svg';
import FeedbackModal from './FeedbackModal/FeedbackModal';
import { ConfigContext } from './ConfigContext';
import { useProfilePictureData } from './ProfilePictureContext';
import { PermissionGate } from './PermissionGate';
import { RBAC_ACTIONS } from '@/utils/permissionGateConfig';
const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    outline: 0
};
export default function LogoutModal() {
    const [open, setOpen] = React.useState(false);
    const [settingsOpen, setSettingsOpen] = React.useState(false);
    const [feedbackOpen, setFeedbackOpen] = React.useState(false);
    const [profilePicture, setProfilePicture] = React.useState(null);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleFeedbackOpen = () => setFeedbackOpen(true);
    const handleFeedbackClose = () => setFeedbackOpen(false);
    const navigate = useNavigate();
    const getCachedProfilePicture = useProfilePictureData();
    const [anchorElLogout, setAnchorElLogout] = React.useState(null);
    const theme = useTheme();
    const { configState } = React.useContext(ConfigContext);
    const [user, setUser] = React.useState(() => {
        try {
            return JSON.parse(Cookies.get('user') ?? '{}');
        }
        catch {
            return {};
        }
    });
    const [tipPosition, setTipPosition] = React.useState('50%');
    const popoverOpen = Boolean(anchorElLogout);
    const [settingsInitialUser, setSettingsInitialUser] = React.useState(user);
    React.useEffect(() => {
        if (popoverOpen && anchorElLogout) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    const anchorRect = anchorElLogout.getBoundingClientRect();
                    const popoverElement = document.querySelector('.MuiPopover-root .MuiPaper-root');
                    if (popoverElement) {
                        const popoverRect = popoverElement.getBoundingClientRect();
                        const anchorCenter = anchorRect.left + anchorRect.width / 2;
                        const popoverLeft = popoverRect.left;
                        const relativePosition = anchorCenter - popoverLeft;
                        const percentage = (relativePosition / popoverRect.width) * 100;
                        const clampedPercentage = Math.max(10, Math.min(90, percentage));
                        setTipPosition(`${clampedPercentage}%`);
                    }
                }, 150);
            });
        }
    }, [popoverOpen, anchorElLogout]);
    React.useEffect(() => {
        if (popoverOpen) {
            globalThis.dispatchEvent(new Event('profileModalOpen'));
        }
        else {
            globalThis.dispatchEvent(new Event('profileModalClose'));
        }
    }, [popoverOpen]);
    const handleProfileMenuOpen = (event) => {
        setAnchorElLogout(event.currentTarget);
    };
    const handleCloseLogout = () => {
        setAnchorElLogout(null);
    };
    const handleSettingsOpen = () => {
        setSettingsInitialUser(user);
        setSettingsOpen(true);
    };
    const handleSettingsClose = () => setSettingsOpen(false);
    const logout = async () => {
        await AuthorizationAPI.logout().then(async () => {
            // Clear IndexedDB cache
            try {
                await clearCache();
            }
            catch (error) {
                console.error('Failed to clear IndexedDB cache on logout:', error);
            }
            // Clear cookies and session storage
            sessionStorage.clear();
            Cookies.remove('access_token');
            Cookies.remove('refresh_token');
            Cookies.remove('user');
            Cookies.remove('ui_trace_id');
            Cookies.remove('theme');
            sessionStorage.clear();
            navigate('/');
            handleClose();
        });
    };
    const handleSettingsSave = async (settings, updatedUser) => {
        if (updatedUser) {
            setUser(updatedUser);
        }
        else {
            // Fallback: attempt to refresh from cookie if update failed
            try {
                const parsed = JSON.parse(Cookies.get('user') ?? '{}');
                setUser(parsed);
            }
            catch {
                // Silently handle parse failure
            }
        }
        handleSettingsClose();
    };
    React.useEffect(() => {
        const userEmail = user?.userAttributes?.email?.trim();
        if (!userEmail) {
            setProfilePicture(null);
            return;
        }
        const fetchProfilePicture = async () => {
            try {
                const profilePictureResponse = await getCachedProfilePicture();
                setProfilePicture(profilePictureResponse);
            }
            catch {
                setProfilePicture(null);
            }
        };
        fetchProfilePicture();
    }, [user?.userAttributes?.email, getCachedProfilePicture]);
    const getInitialsFromCommaFormat = (name) => {
        const parts = name
            ?.split(',')
            .map((part) => part.trim())
            .filter(Boolean);
        if (!parts || parts.length === 0) {
            return '';
        }
        if (parts.length > 1 && parts[0] && parts[1]) {
            const first = parts[1][0]?.toUpperCase() || '';
            const second = parts[0][0]?.toUpperCase() || '';
            return `${first}${second}`;
        }
        return parts[0][0]?.toUpperCase() || '';
    };
    const getInitialsFromSpaceFormat = (name) => {
        const parts = name?.split(' ').filter((part) => part.length > 0);
        if (!parts || parts.length === 0) {
            return '';
        }
        if (parts.length >= 2) {
            return `${parts[0][0].toUpperCase()}${parts.at(-1)?.[0].toUpperCase() ?? ''}`;
        }
        return parts[0][0].toUpperCase();
    };
    const getInitials = () => {
        const name = user?.userAttributes?.name?.trim();
        if (!name) {
            return user?.username?.[0]?.toUpperCase() ?? '';
        }
        if (name.includes('@'))
            return name[0].toUpperCase() ?? '';
        if (name.includes(','))
            return getInitialsFromCommaFormat(name);
        if (name.includes(' '))
            return getInitialsFromSpaceFormat(name);
        return name[0].toUpperCase();
    };
    return (_jsxs(_Fragment, { children: [_jsx(Button, { "aria-label": "account of current user", "aria-haspopup": "true", onClick: handleProfileMenuOpen, "data-testid": "logout-details", sx: {
                    p: 0,
                    mr: -0.5,
                    backgroundColor: '#EBEBEB',
                    borderRadius: '50%',
                    minWidth: '32px',
                    minHeight: '32px',
                    width: '32px',
                    height: '32px',
                    color: '#595959'
                }, variant: "text", children: _jsx(Avatar, { src: profilePicture ?? undefined, imgProps: {
                        onError: () => setProfilePicture(null)
                    }, sx: {
                        bgcolor: '#e0e0e0',
                        color: '#424242',
                        fontWeight: 600,
                        fontStyle: 'SemiBold',
                        lineHeight: '152%',
                        letterSpacing: '0.17px',
                        fontSize: '14px',
                        fontFamily: theme.typography.fontFamily,
                        width: 32,
                        height: 32
                    }, children: getInitials() }) }), _jsx(Popover, { open: Boolean(anchorElLogout), anchorEl: anchorElLogout, onClose: handleCloseLogout, disableRestoreFocus: true, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, transformOrigin: { vertical: 'top', horizontal: 'center' }, slotProps: {
                    paper: {
                        style: {
                            borderRadius: '16px',
                            overflow: 'visible',
                            marginTop: '25px'
                        },
                        sx: {
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: -9,
                                left: tipPosition,
                                transform: 'translateX(-50%)',
                                width: 14,
                                height: 10,
                                background: 'transparent',
                                zIndex: 2,
                                pointerEvents: 'none',
                                WebkitClipPath: 'polygon(46% 0%, 54% 0%, 100% 100%, 0% 100%)',
                                clipPath: 'path("M0 100% L46% 10% Q50% 0% 54% 10% L100% 100% Z")',
                                backgroundColor: '#fff',
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
                            }
                        }
                    }
                }, children: _jsxs(List, { sx: {
                        width: '300px',
                        bgcolor: 'background.paper',
                        borderRadius: '16px'
                    }, children: [_jsx(ListItem, { children: _jsxs(Box, { display: "flex", alignItems: "center", gap: 2, children: [_jsx(Avatar, { src: profilePicture ?? undefined, imgProps: {
                                            onError: () => setProfilePicture(null)
                                        }, sx: {
                                            bgcolor: '#e0e0e0',
                                            color: '#424242',
                                            fontWeight: 600,
                                            fontStyle: 'SemiBold',
                                            lineHeight: '152%',
                                            letterSpacing: '0.17px',
                                            fontSize: '14px',
                                            fontFamily: theme.typography.fontFamily
                                        }, children: getInitials() }), _jsxs(Stack, { children: [_jsx(Typography, { variant: "body1", fontWeight: "400", color: "#212121", children: user?.userAttributes?.name }), _jsx(Typography, { variant: "caption", color: "#595959", fontWeight: 400, children: user?.userAttributes?.email })] })] }) }), _jsx(Divider, { sx: { my: 1 } }), _jsx(PermissionGate, { action: [RBAC_ACTIONS.RBAC_VIEW_APPLICATION], children: _jsx(ListItem, { disablePadding: true, onClick: () => {
                                    window.open(ROUTES.HELP, '_blank');
                                    handleCloseLogout();
                                }, children: _jsxs(ListItemButton, { children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx("img", { src: HelpCenterIcon, alt: "HelpCenter", style: { width: 24, height: 24 } }) }), _jsx(ListItemText, { primary: constants.help_center_title })] }) }) }), _jsx(PermissionGate, { action: [RBAC_ACTIONS.RBAC_UPDATE_FEEDBACK], children: _jsxs(ListItemButton, { onClick: () => {
                                    handleFeedbackOpen();
                                    handleCloseLogout();
                                }, children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx("img", { src: ReviewsIcon, alt: "Reviews", style: { width: 24, height: 24 } }) }), _jsx(ListItemText, { primary: constants.feedback })] }) }), _jsx(PermissionGate, { action: [RBAC_ACTIONS.RBAC_VIEW_USER], children: _jsx(ListItem, { disablePadding: true, onClick: () => {
                                    handleCloseLogout();
                                    handleSettingsOpen();
                                }, "data-testid": "settings-item-user", children: _jsxs(ListItemButton, { children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx("img", { src: SettingsIcon, alt: "Settings", style: { width: 24, height: 24 } }) }), _jsx(ListItemText, { primary: constants.settings_title })] }) }) }), _jsx(Divider, { sx: { my: 1 } }), _jsx(ListItem, { disablePadding: true, onClick: () => {
                                handleOpen();
                                handleCloseLogout();
                            }, "data-testid": "logout-item", children: _jsxs(ListItemButton, { children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx(LogoutIcon, {}) }), _jsx(ListItemText, { primary: constants.logout })] }) })] }) }), _jsx(Modal, { open: open, onClose: handleClose, "aria-labelledby": "modal-modal-title", "aria-describedby": "modal-modal-description", children: _jsxs(Paper, { sx: style, children: [_jsx(Typography, { id: "modal-modal-title", variant: "body1", children: constants.exit_msg }), _jsxs(Stack, { direction: "row", spacing: 2, paddingTop: 2, children: [_jsx(Button, { variant: "outlined", onClick: handleClose, "data-testid": "logout-cancel", sx: {
                                        borderColor: configState?.settings?.button_color,
                                        color: configState?.settings?.button_color,
                                        '&:hover': {
                                            borderColor: configState?.settings?.button_color
                                        }
                                    }, children: constants.cancel }), _jsx(Button, { variant: "contained", onClick: () => {
                                        logout();
                                    }, "data-testid": "logout-logout", sx: {
                                        bgcolor: configState?.settings?.button_color
                                    }, children: constants.logout })] })] }) }), _jsx(FeedbackModal, { open: feedbackOpen, onClose: handleFeedbackClose }), _jsx(PermissionGate, { action: [RBAC_ACTIONS.RBAC_UPDATE_USER], behavior: "grey", children: _jsx(SettingsModal, { open: settingsOpen, onClose: handleSettingsClose, onUpdate: handleSettingsSave, initialUser: settingsInitialUser }) })] }));
}
